/* eslint-disable eqeqeq */
import React from "react";
import { ButtonGroup, Card, Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router";
import { Button, Input, DropDown } from "../../components";
import { FacilityDetailType } from "../../utilities/types";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppSelector } from "../../redux/Hooks/Hook";
import { messages } from "../../utilities/Messages";

const getMessage = messages.facilities
const facilitySchema = yup.object().shape(
  {
    addressLine1: yup
      .string()
      .required(getMessage.addressLine1_isNotEmpty)
      .max(50, getMessage.addressLine1_maxLength),
    addressLine2: yup
      .string()
      .max(50, getMessage.addressLine2_maxLength),
    landmark: yup.string().max(50, getMessage.landmark_maxLength),

    name: yup
      .string()
      .required(getMessage.facilityName_isNotEmpty)
      .matches(
        /^[a-zA-Z0-9\s\-.@(),/&#]*$/,
        getMessage.facility_matches
      )
      .min(3, getMessage.facility_minLength)
      .max(30, getMessage.facility_maxLength),

    pincode: yup
      .string()
      .required(getMessage.pincode_isNotEmpty)
      .matches(/^[1-9][0-9]{5}$/, getMessage.pincode_maxLength),

    city: yup
      .string()
      .required(getMessage.city_isNotEmpty)
      .max(50, getMessage.city_maxLength),


    state: yup
      .string()
      .required(getMessage.state_isNotEmpty)
      .max(50, getMessage.state_maxLength),



    latitude: yup.string().when(getMessage.latitude_name, ([latitude], sch) => {
      return latitude != ""
        ? sch
          .max(12, getMessage.latitude_maxLength)
          .matches(
            /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/,
            getMessage.latitude_matches
          )
        : sch.required();
    }),

    longitude: yup.string().when(getMessage.longitude_name, ([longitude], sch) => {
      return longitude != ""
        ? sch
          .max(12, getMessage.longitude_maxLength)
          .matches(
            /^[-+]?((1[0-7]\d(\.\d+)?)|([1-9]?\d(\.\d+)?)|180(\.0+)?)$/,
            getMessage.longitude_matches
          )
        : sch.required();
    }),

  },
  [
    ["latitude", "latitude"],
    ["longitude", "longitude"],
  ]
);

const AddUpdateFacilities = (props: any) => {
  const {
    name,
    facilityData,
    handleCreateUpdate,
    setFacilityData,
    submitButton,
  } = props;

  const { states } = useAppSelector((state) => state.states);
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FacilityDetailType>({
    resolver: yupResolver(facilitySchema),
  });

  const navigate = useNavigate();

  const handleCancel = () => {
    navigate("/facilities");
  };

  const onSubmit: SubmitHandler<FacilityDetailType> = (data) => {
    handleCreateUpdate({
      ...facilityData,
      ...data,
      latitude: Number(data.latitude ? data.latitude : ""),
      longitude: Number(data.longitude ? data.longitude : ""),
    });
  };


  return (
    <Container fluid className="g-0">
      <Card>
        <Card.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Card.Title className="text-start">{name}</Card.Title>
            <Row>
              <Col col={6}>
                <div className="mb-3 text-start">
                  <label htmlFor="facilityname" className="form-label">
                    Facility Name <small className="text-danger">*</small>
                  </label>
                  <Input
                    defaultValue={facilityData?.name}
                    type="text"
                    placeholder="Enter Facility Name"
                    id="facilityname"
                    name="name"
                    className=" form-control"
                    register={register}
                    required
                  />
                  {errors.name && <span className="error-msg">{errors.name.message}</span>}
                </div>
              </Col>
            </Row>
            <Row>
              <Col col={6}>
                <div className="mb-3 text-start">
                  <label htmlFor="addressLine1" className="form-label">
                    Address line 1 <small className="text-danger">*</small>
                  </label>
                  <Input
                    defaultValue={facilityData?.addressLine1}
                    register={register}
                    required
                    type="text"
                    placeholder="Enter Address"
                    id="addressLine1"
                    name="addressLine1"
                    className="form-control"
                  />
                  {errors.addressLine1 && (
                    <span className="error-msg">{errors.addressLine1.message}</span>
                  )}
                </div>
              </Col>
              <Col col={6}>
                <div className="mb-3 text-start">
                  <label htmlFor="addressLine2" className="form-label">
                    Address line 2
                  </label>
                  <Input
                    defaultValue={facilityData?.addressLine2}
                    register={register}
                    type="text"
                    placeholder="Enter Address"
                    id="addressLine2"
                    name="addressLine2"
                    className="form-control"
                  />
                  {errors.addressLine2 && (
                    <span className="error-msg">{errors.addressLine2.message}</span>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              <Col col={6}>
                <div className="mb-3 text-start">
                  <label htmlFor="landmark" className="form-label">
                    Landmark{" "}
                  </label>
                  <Input
                    defaultValue={facilityData?.landmark}
                    register={register}
                    type="text"
                    placeholder="Enter Landmark"
                    id="landmark"
                    name="landmark"
                    className="form-control"
                  />
                  {errors.landmark && <span className="error-msg">{errors.landmark.message}</span>}
                </div>
              </Col>
              <Col col={6}>
                <div className="mb-3 text-start">
                  <label htmlFor="pincode" className="form-label">
                    Pincode <small className="text-danger">*</small>
                  </label>
                  <Input
                    defaultValue={facilityData?.pincode}
                    register={register}
                    required
                    onKeyDown={(e: any) =>
                      ["e", ".", "-", "+"].includes(e.key) && e.preventDefault()
                    }
                    onWheel={(e: any) => e.target.blur()}
                    type="number"
                    placeholder="Enter Pincode"
                    id="pincode"
                    name="pincode"
                    className="form-control"
                  />
                  {errors.pincode && <span className="error-msg">{errors.pincode.message}</span>}
                </div>
              </Col>
            </Row>
            <Row>
              <Col col={6}>
                <div className="mb-3 text-start">
                  <label htmlFor="city" className="form-label">
                    City <small className="text-danger">*</small>
                  </label>
                  <Input
                    defaultValue={facilityData?.city}
                    register={register}
                    required
                    type="text"
                    placeholder="Enter City"
                    id="city"
                    name="city"
                    className="form-control"
                  />
                  {errors.city && <span className="error-msg">{errors.city.message}</span>}
                </div>
              </Col>
              <Col col={6}>
                <div className="mb-3 text-start">
                  <label htmlFor="state" className="form-label">
                    State <small className="text-danger">*</small>
                  </label>
                  <DropDown
                    defaultValue={facilityData?.state}
                    register={register}
                    required
                    name="state"
                    id="state"
                    className="form-control"
                  >
                    <option hidden disabled value="">
                      Select State
                    </option>
                    {states?.map((item: any) => {
                      return (
                        <option key={item.code} value={item.name}>
                          {item.name}
                        </option>
                      );
                    })}
                  </DropDown>
                  {errors.state && <span className="error-msg">{errors.state.message}</span>}
                </div>
              </Col>
            </Row>
            <Row>
              <Col col={6}>
                <div className="mb-3 text-start">
                  <label htmlFor="latitude" className="form-label">
                    Latitude <small className="text-danger">*</small>
                  </label>
                  <Input
                    defaultValue={facilityData?.latitude}
                    register={register}
                    required
                    type="text"
                    placeholder="Enter Latitude"
                    id="latitude"
                    name="latitude"
                    className="form-control"
                  />
                  <span className="error-msg">{errors?.latitude?.message}</span>
                </div>
              </Col>
              <Col col={6}>
                <div className="mb-3 text-start">
                  <label htmlFor="longitude" className="form-label">
                    Longitude <small className="text-danger">*</small>
                  </label>
                  <Input
                    defaultValue={facilityData?.longitude}
                    register={register}
                    required
                    type="text"
                    placeholder="Enter Longitude"
                    id="longitude"
                    name="longitude"
                    className="form-control"
                  />
                  <span className="error-msg">{errors?.longitude?.message}</span>
                </div>
              </Col>
            </Row>
            {name === "Edit Facility" && (
              <Row>
                <Col col={6}>
                  <div className="mb-3 text-start">
                    <label htmlFor="facilityname" className="form-label">
                      Status <small className="text-danger">*</small>
                    </label>
                    <DropDown
                      value={facilityData?.isActive}
                      onChange={(e: any) =>
                        setFacilityData({
                          ...facilityData,
                          isActive: e.target.value,
                        }) as string
                      }
                      name="isActive"
                      id="isActive"
                      className="form-control"
                    >
                      <option hidden disabled value="">
                        Select Facility Status
                      </option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </DropDown>
                    {errors.isActive && (
                      <span className="error-msg">{errors.isActive.message}</span>
                    )}
                  </div>
                </Col>
                <Col col={6}></Col>
              </Row>
            )}
            <hr />

            <Row>
              <Col className="text-end">
                <ButtonGroup>
                  <Button
                    type="button"
                    onClick={handleCancel}
                    bname="Cancel"
                    name="cancel"
                    id="cancel"
                    className="btn btn-light rounded-1 me-3"
                  ></Button>
                  <Button
                    type="submit"
                    disabled={submitButton}
                    bname={
                      name === "Add Facility"
                        ? submitButton
                          ? "Submitting"
                          : "Submit"
                        : submitButton
                          ? "Updating"
                          : "Update"
                    }
                    name={name === "addfacilitty" ? "addfacilitty" : "update"}
                    id={name === "addfacilitty" ? "addfacilitty" : "update"}
                    className="btn btn-primary rounded-1"
                  ></Button>
                </ButtonGroup>
              </Col>
            </Row>
          </form>
        </Card.Body>
      </Card>
    </Container>
  );
};
export { AddUpdateFacilities };
